import type {
  ComputedElementStyles,
  SemanticElement,
} from "schemas/generated/semanticElement";

import React from "react";

import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { createContext, useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { z } from "zod";
import { createStore } from "zustand";

export type DisplayMode = "interactive" | "select";

interface ComputedElementDimensions {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface PendingPreview {
  selector: string;
  newHtml: string;
  createdAt: number;
  pageRoute?: string;
  sessionUuid?: string;
  containerOpeningTag?: string;
}

function getPendingPreviewStorageKey(projectId: string) {
  return `replo.pendingPreviews.${projectId}`;
}

const pendingPreviewSchema = z.object({
  selector: z.string(),
  newHtml: z.string(),
  createdAt: z.number(),
  pageRoute: z.string().optional(),
  sessionUuid: z.string().optional(),
  containerOpeningTag: z.string().optional(),
});

// NOTE (Derek, 2026-02-09): Previews persist in sessionStorage so they survive page
// refreshes and are reapplied until FAST_REFRESH_COMPLETE clears them after HMR.
function readPendingPreviewsFromStorage(projectId: string): PendingPreview[] {
  const storageKey = getPendingPreviewStorageKey(projectId);
  const raw = window.sessionStorage.getItem(storageKey);
  if (!raw) {
    return [];
  }
  const parsed = parseJsonOrNull(raw);
  const result = z.array(pendingPreviewSchema).safeParse(parsed);
  return result.success ? result.data : [];
}

function writePendingPreviewsToStorage(
  projectId: string,
  previews: PendingPreview[],
) {
  const storageKey = getPendingPreviewStorageKey(projectId);
  if (previews.length === 0) {
    window.sessionStorage.removeItem(storageKey);
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(previews));
  } catch (error: unknown) {
    if (
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" || error.name === "SecurityError")
    ) {
      return;
    }
    throw error;
  }
}

// NOTE (Derek, 2026-02-11): Merges inline styles from two container opening tags so that
// accumulated modifier changes (e.g. width then height) survive instead of the newer
// preview replacing all styles from the earlier one.
function mergeContainerOpeningTagStyles(
  existingTag: string,
  newTag: string,
): string {
  const parseStyle = (tag: string): string => {
    const temp = document.createElement("div");
    temp.innerHTML = `${tag}</div>`;
    return temp.firstElementChild?.getAttribute("style") ?? "";
  };

  const existingStyle = parseStyle(existingTag);
  const newStyle = parseStyle(newTag);

  if (!existingStyle) {
    return newTag;
  }

  const merged = document.createElement("div");
  merged.style.cssText = existingStyle;
  const overlay = document.createElement("div");
  overlay.style.cssText = newStyle;
  for (let i = 0; i < overlay.style.length; i++) {
    const prop = overlay.style[i];
    if (!prop) {
      continue;
    }
    merged.style.setProperty(prop, overlay.style.getPropertyValue(prop));
  }

  const temp = document.createElement("div");
  temp.innerHTML = `${newTag}</div>`;
  const el = temp.firstElementChild;
  if (!el) {
    return newTag;
  }
  el.setAttribute("style", merged.style.cssText);

  const outer = el.outerHTML;
  const closingTag = `</${el.tagName.toLowerCase()}>`;
  if (outer.endsWith(closingTag)) {
    return outer.slice(0, -closingTag.length);
  }
  return newTag;
}

interface ModifiersState {
  displayMode: DisplayMode;
  iframeContainer: HTMLDivElement | null;
  isIframeLoaded: boolean;
  isDeleting: boolean;
  computedElementDimensions: ComputedElementDimensions | null;
  // NOTE (Yuxin, 2026-02-02): New semantic element state for LLM-based editing
  semanticElement: SemanticElement | null;
  // NOTE (Derek, 2026-02-05): Pending previews to reapply after iframe refresh
  pendingPreviews: PendingPreview[];
  actions: {
    setDisplayMode: (displayMode: DisplayMode) => void;
    toggleDisplayMode: () => void;
    resetToDefaults: () => void;
    setIframeContainer: (element: HTMLDivElement | null) => void;
    setIframeLoaded: (loaded: boolean) => void;
    setIsDeleting: (isDeleting: boolean) => void;
    setComputedElementDimensions: (
      dimensions: ComputedElementDimensions | null,
    ) => void;
    // NOTE (Yuxin, 2026-02-02): New semantic element actions for LLM-based editing
    setSemanticElement: (element: SemanticElement | null) => void;
    clearSemanticSelection: () => void;
    // NOTE (Derek, 2026-02-12): Consolidates clearing stale selection and rects
    // when IFRAME_READY fires, to avoid scattered setter calls in the message router.
    resetStateForIframeLoad: () => void;
    addPendingPreview: (preview: PendingPreview) => void;
    removePendingPreview: (selector: string) => void;
    removePendingPreviewIfMatch: (params: {
      selector: string;
      createdAt: number;
    }) => void;
    clearPendingPreviews: () => void;
    // NOTE (Derek, 2026-02-11): Merge updated styles/bounds after APPLY_PREVIEW
    // without clearing fiber selection or other state (unlike setSemanticElement)
    updateSemanticElementAfterPreview: (params: {
      boundingBox: { x: number; y: number; width: number; height: number };
      computedStyles: ComputedElementStyles;
      openingTag?: string;
    }) => void;
  };
}

const createModifiersStore = (projectId: string) => {
  return createStore<ModifiersState>((set, get) => ({
    displayMode: "select",
    iframeContainer: null,
    // NOTE (Derek, 2026-02-09): Default to false so the iframe is hidden (opacity: 0) on page
    // refresh until IFRAME_READY fires, preventing the stale content flash.
    isIframeLoaded: false,
    isDeleting: false,
    computedElementDimensions: null,
    // NOTE (Yuxin, 2026-02-02): New semantic element state for LLM-based editing
    semanticElement: null,
    // NOTE (Derek, 2026-02-09): Read synchronously so the very first render has
    // patches applied to the cached HTML overlay, preventing any stale flash.
    pendingPreviews: readPendingPreviewsFromStorage(projectId),
    actions: {
      setDisplayMode: (displayMode: DisplayMode) => {
        return set({ displayMode, semanticElement: null });
      },
      toggleDisplayMode: () => {
        const current = get().displayMode;
        set({
          displayMode: current === "select" ? "interactive" : "select",
          semanticElement: null,
        });
      },
      resetToDefaults: () => {
        return set({
          displayMode: "select",
          // NOTE (Yuxin, 2026-02-03): Keep as true since sandbox is always available
          isIframeLoaded: true,
          computedElementDimensions: null,
          semanticElement: null,
        });
      },
      setIframeContainer: (element: HTMLDivElement | null) => {
        return set({ iframeContainer: element });
      },
      setIframeLoaded: (loaded: boolean) => set({ isIframeLoaded: loaded }),
      setIsDeleting: (isDeleting: boolean) => set({ isDeleting }),
      setComputedElementDimensions: (
        dimensions: ComputedElementDimensions | null,
      ) => {
        return set({ computedElementDimensions: dimensions });
      },
      setSemanticElement: (element: SemanticElement | null) => {
        set({ semanticElement: element });
      },
      clearSemanticSelection: () => {
        set({ semanticElement: null });
      },
      resetStateForIframeLoad: () => {
        return set({
          computedElementDimensions: null,
          isIframeLoaded: true,
          semanticElement: null,
        });
      },
      addPendingPreview: (preview: PendingPreview) => {
        const existingForSelector = get().pendingPreviews.find(
          (p) => p.selector === preview.selector,
        );
        const others = get().pendingPreviews.filter(
          (p) => p.selector !== preview.selector,
        );

        // NOTE (Derek, 2026-02-11): For container previews on the same selector,
        // merge inline styles so accumulated changes (width then height) survive.
        let merged = preview;
        if (
          existingForSelector?.containerOpeningTag &&
          preview.containerOpeningTag
        ) {
          merged = {
            ...preview,
            containerOpeningTag: mergeContainerOpeningTagStyles(
              existingForSelector.containerOpeningTag,
              preview.containerOpeningTag,
            ),
          };
        }

        const pendingPreviews = [...others, merged];
        set({ pendingPreviews });
        writePendingPreviewsToStorage(projectId, pendingPreviews);
      },
      removePendingPreview: (selector: string) => {
        const pendingPreviews = get().pendingPreviews.filter(
          (p) => p.selector !== selector,
        );
        set({
          pendingPreviews,
        });
        writePendingPreviewsToStorage(projectId, pendingPreviews);
      },
      removePendingPreviewIfMatch: ({
        selector,
        createdAt,
      }: {
        selector: string;
        createdAt: number;
      }) => {
        const existing = get().pendingPreviews.find(
          (p) => p.selector === selector,
        );
        if (!existing || existing.createdAt !== createdAt) {
          return;
        }
        const pendingPreviews = get().pendingPreviews.filter(
          (p) => p.selector !== selector,
        );
        set({
          pendingPreviews,
        });
        writePendingPreviewsToStorage(projectId, pendingPreviews);
      },
      clearPendingPreviews: () => {
        set({ pendingPreviews: [] });
        writePendingPreviewsToStorage(projectId, []);
      },
      updateSemanticElementAfterPreview: ({
        boundingBox,
        computedStyles,
        openingTag,
      }: {
        boundingBox: { x: number; y: number; width: number; height: number };
        computedStyles: ComputedElementStyles;
        openingTag?: string;
      }) => {
        const current = get().semanticElement;
        if (!current) {
          return;
        }
        set({
          semanticElement: {
            ...current,
            boundingBox,
            computedStyles,
            ...(openingTag ? { openingTag } : {}),
          },
        });
      },
    },
  }));
};

type ModifiersContextType = {
  store: ReturnType<typeof createModifiersStore>;
};

const ModifiersContext = createContext<ModifiersContextType | null>(
  "ModifiersContext",
  null,
);

export const useModifiersContext = () => {
  return useRequiredContext(ModifiersContext);
};

export function ModifiersProvider({ children }: React.PropsWithChildren) {
  const projectId = useCurrentProjectId();

  // NOTE (Derek, 2026-02-09): pendingPreviews are read synchronously from sessionStorage
  // in createModifiersStore, so no useEffect needed to hydrate them.
  const [store] = React.useState(() => {
    return createModifiersStore(projectId ?? "unknown");
  });

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ModifiersContext.Provider value={contextValue}>
      {children}
    </ModifiersContext.Provider>
  );
}

// NOTE (Kurt, 2025-07-16): Main hook to access the store, only use this if you are sure you
// need to access the entire store. For most cases, use the convenience hooks below.
function useModifiersStore<T>(selector: (state: ModifiersState) => T): T {
  const { store } = useModifiersContext();
  return useShallowStore(store, selector);
}

export function useIsAnyElementSelected() {
  return useModifiersStore((state) => state.semanticElement !== null);
}

export function useModifiersStoreActions() {
  return useModifiersStore((state) => state.actions);
}

export function useDisplayMode() {
  return useModifiersStore((state) => state.displayMode);
}

export function useIsIframeLoaded() {
  return useModifiersStore((state) => state.isIframeLoaded);
}

export function useIsDeleting() {
  return useModifiersStore((state) => state.isDeleting);
}

export function useComputedElementDimensions() {
  return useModifiersStore((state) => state.computedElementDimensions);
}

export function useSemanticElement() {
  return useModifiersStore((state) => state.semanticElement);
}

export function usePendingPreviews() {
  return useModifiersStore((state) => state.pendingPreviews);
}

export function useHasSemanticSelection() {
  return useModifiersStore((state) => state.semanticElement !== null);
}

export function useSelectedElementContent() {
  return useModifiersStore((state) => {
    if (state.semanticElement) {
      return {
        text: state.semanticElement.text ?? "",
        computedStyles: state.semanticElement.computedStyles ?? {},
        className: state.semanticElement.cssClasses ?? "",
        source: "semantic" as const,
      };
    }

    return null;
  });
}
